import React, {useCallback, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import api from '../../../../api'
import ComponentTooltip from '../../ComponentTooltip'
import ComponentButton, {getLabel} from '..'
import {getErrorToast} from '../../../helpers/toasts'
import {TComponentButtonEgiszProtocol} from '../_types'
import {formatEgiszGapLabel} from './egiszFieldLabels'

type TEgiszDocument = {
    slug: string
    title: string
    code: string
}

const ComponentButtonEgiszProtocol: React.FC<TComponentButtonEgiszProtocol> = ({settings, defaultLabel = 'title'}) => {
    const intl = useIntl()
    const [show, setShow] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [documents, setDocuments] = useState<TEgiszDocument[]>([])
    const [gaps, setGaps] = useState<string[] | null>(null)

    const openModal = useCallback(async () => {
        setGaps(null)
        setIsLoading(true)
        try {
            const response = await api('visitReports', 'egiszCatalog', {visit_id: settings.data.row_id})
            const existingDraft = response.data?.existing_draft
            if (existingDraft?.fill_url) {
                window.open(existingDraft.fill_url, '_blank')
                return
            }
            setShow(true)
            setDocuments(response.data?.documents ?? [])
        } catch (error: any) {
            getErrorToast(error.message)
        } finally {
            setIsLoading(false)
        }
    }, [settings.data.row_id])

    const handleSelect = useCallback(async (slug: string) => {
        setIsSubmitting(true)
        setGaps(null)
        try {
            const response = await api('visitReports', 'prepareEgiszDraft', {
                visit_id: settings.data.row_id,
                cemd_slug: slug,
            })
            const fillUrl = response.data?.fill_url
            if (fillUrl) {
                window.open(fillUrl, '_blank')
                setShow(false)
            } else {
                getErrorToast('egisz не вернул ссылку на заполнение')
            }
        } catch (error: any) {
            const gapsFromError = error?.__APP_PAYLOAD__?.data?.gaps ?? error?.data?.data?.gaps
            if (gapsFromError) {
                setGaps(gapsFromError)
            } else {
                getErrorToast(error.message)
            }
        } finally {
            setIsSubmitting(false)
        }
    }, [settings.data.row_id])

    return (
        <>
            <ComponentTooltip title={settings.title ?? ''} show={defaultLabel === 'icon' ? undefined : false}>
                <button
                    className={`componentButton ${settings.background}`}
                    type="button"
                    onClick={openModal}
                >
                    {getLabel(defaultLabel, settings)}
                </button>
            </ComponentTooltip>

            <Modal show={show} onHide={() => setShow(false)} size="sm" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Отправить в ЕГИСЗ</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {gaps ? (
                        <>
                            <p>Не хватает данных для отправки. Проверьте карточку клиента/сотрудника:</p>
                            <ul>
                                {gaps.map((g) => (
                                    <li key={g} title={g}>{formatEgiszGapLabel(g)}</li>
                                ))}
                            </ul>
                        </>
                    ) : isLoading ? (
                        <p>Загрузка списка документов...</p>
                    ) : documents.length === 0 ? (
                        <p>Нет доступных типов СЭМД</p>
                    ) : (
                        <div className="d-flex flex-column gap-2">
                            {documents.map((doc) => (
                                <ComponentButton
                                    key={doc.slug}
                                    type="custom"
                                    disabled={isSubmitting}
                                    settings={{title: doc.title, background: 'dark', icon: ''}}
                                    customHandler={() => handleSelect(doc.slug)}
                                />
                            ))}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    )
}

export default ComponentButtonEgiszProtocol